<template>
  <div>
    <div class="mb-2 d-flex align-items-center gap-2">
      <router-link
        :to="{ name: 'cost-prices-consignor-prices' }"
        class="d-flex align-items-center gap-2"
      >
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
        {{ t('Back to List') }}
      </router-link>
    </div>

    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="formObserver">
        <b-form ref="formEl" @submit.prevent="submit">
          <b-row>
            <b-col sm="12" md="6">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="provider">{{ t('Provider') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Provider')" rules="required">
                  <v-select
                    id="provider"
                    v-model="values.providerId"
                    :options="providerOptions"
                    :reduce="item => item.value"
                    :placeholder="t('Make a Choice')"
                    label="label"
                    :disabled="!isCreate ? true : false"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="12" md="6">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="price_value">{{ t('Return Rate') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Return Rate')"
                  rules="required|doubleOrInteger|greaterThanZero"
                >
                  <b-form-input
                    id="price_value"
                    v-model.number="values.price"
                    type="number"
                    :step="0.01"
                    :min="0"
                    :placeholder="isCreate ? t('Return-to-sender rate') : t('Enter Value')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <div
                class="d-flex gap-3 align-items-center justify-content-end mt-2"
                style="gap: 12px"
              >
                <b-button variant="primary" class="d-flex align-items-center" type="submit">
                  {{ submitting ? t('Please Wait') : t('Save') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>

<script>
import { BCol, BForm, BFormGroup, BRow, BButton, BOverlay, BFormInput } from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import vSelect from 'vue-select';
import useService from '@/auth/service/useService';

export default {
  name: 'EditCostPricesConsignorPrice',
  components: {
    BOverlay,
    BButton,
    BFormGroup,
    BRow,
    BCol,
    BForm,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    vSelect
  },
  mixins: [helper],
  data() {
    return {
      submitting: false,
      values: {
        providerId: null,
        price: null
      },
      providerOptions: [],
      providerReturnFeeId: null
    };
  },
  methods: {
    async loadData() {
      const { providerId } = this.$route.params;
      this.submitting = true;
      try {
        const providersRes = await useService
          .getAllProviders({ paginated: false })
          .catch(() => ({ data: [] }));

        this.providerOptions = (providersRes.data || []).map(item => ({
          value: item.id,
          label: item.name
        }));

        if (providerId) {
          const feeRes = await useService.getCustomerReturnFee(providerId);
          const fee = Array.isArray(feeRes?.data) ? feeRes.data[0] : feeRes?.data;
          this.providerReturnFeeId = fee?.id ?? null;
          this.values.providerId = fee?.provider?.id || parseInt(providerId);
          this.values.price = fee?.price ?? null;
        } else {
          this.providerReturnFeeId = null;
          this.values.providerId = null;
          this.values.price = null;
        }
      } catch (messages) {
        if (typeof messages === 'object' && messages && messages.length) {
          messages.forEach(item => this.showErrorMessage(item.message));
        }
      } finally {
        this.submitting = false;
      }
    },
    submit() {
      this.$refs.formObserver.validate().then(async success => {
        if (!success) return;
        const { providerId } = this.$route.params;
        this.submitting = true;
        try {
          const args = { price: this.values.price };
          if (this.providerReturnFeeId) {
            await useService.editCustomerReturnFee(
              this.values.providerId || parseInt(providerId),
              this.providerReturnFeeId,
              args
            );
          } else {
            await useService.createCustomerReturnFee(
              this.values.providerId || parseInt(providerId),
              args
            );
          }
          this.showSuccessMessage(this.$t('Successful'));
          this.$router.push({ name: 'cost-prices-consignor-prices' });
        } catch (messages) {
          if (typeof messages === 'object' && messages && messages.length) {
            messages.forEach(item => this.showErrorMessage(item.message));
          }
        } finally {
          this.submitting = false;
        }
      });
    }
  },
  created() {
    this.loadData();
  },
  computed: {
    isCreate() {
      return !this.$route.params.providerId;
    }
  },
  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss" scoped>
.form-group-label {
  margin-bottom: 0.5rem;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
